import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useGetCampaignContent, usePreviewCampaign } from "../../../../../queries";
import { ProgressSpinner } from "primereact/progressspinner";
import LazyImage from "../../../../SharedComponents/ShowMedia/LazyImage";
import { formatDuration } from "../../../../../config/global";
import { useEffect, useState, useMemo, useRef } from "react";
import moment from "moment";
import PlaylistContent from "../../../../Devices/Component/Preview/Content/PlaylistContent";
import LayoutsContent from "../../../../Devices/Component/Preview/Content/LayoutsContent";
import ContentPreview, { PREVIEW_AREA_ID } from "./ContentPreview";
import { useSelector, useDispatch } from "react-redux";
import { setPaused } from "../../../../../redux/Slices/deviceSlice";

// ---------------------------------------------------------------------------
// Sidebar item component — plain div, no nested Accordion wrappers
// ---------------------------------------------------------------------------
function SidebarItem({ index, item, isActive, tc, hasChildren, tick, isPaused, pausedElapsedRef, startTime, getProgressStyle, currentItem, onSelect }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    return (
        <div key={index} className="flex flex-col">
            {/* Card row */}
            <div
                className={`
                    relative flex items-center gap-3 w-full px-3 py-2.5
                    rounded-xl border cursor-pointer
                    transition-all duration-200 overflow-hidden group
                    ${isActive
                        ? 'border-[var(--pp-btn-honey)] bg-[color-mix(in_srgb,var(--pp-btn-honey)_10%,transparent)] shadow-md'
                        : 'border-[var(--pp-border)] bg-[var(--pp-bg-elevated)] hover:border-[var(--pp-btn-honey)] shadow-sm hover:shadow'
                    }
                `}
                style={isActive ? getProgressStyle(startTime, item.custom_duration) : {}}
                onClick={() => {
                    onSelect();
                    if (hasChildren) setExpanded(p => !p);
                }}
            >
                {/* Active left accent bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl transition-all duration-300 ${isActive ? 'bg-[var(--pp-btn-honey)]' : 'bg-transparent group-hover:bg-slate-200'}`} />

                {/* Index number */}
                <div className={`
                    flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ml-1
                    transition-all duration-200
                    ${isActive ? 'bg-[var(--pp-btn-honey)] text-white shadow-sm' : 'bg-[var(--pp-bg-tertiary)] text-[var(--pp-text-secondary)]'}
                `}>
                    {isActive
                        ? <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse inline-block" />
                        : index + 1
                    }
                </div>

                {/* Thumbnail */}
                <div className={`flex-shrink-0 w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center border ${tc.bg} ${tc.border} transition-all duration-200`}>
                    {item.media_type && ["HDMI", "CHANNEL", "RSS", "RSS_CUSTOM", "MULTI_3D"].includes(item.media_type) ? (
                        <span className={`text-[7px] font-black px-0.5 uppercase tracking-widest ${tc.text}`}>{item.media_type.replace(/_/g, ' ')}</span>
                    ) : item.original_media_type === "WEATHER" ? (
                        <i className="fa-solid fa-cloud-sun text-sm text-amber-400" />
                    ) : item.original_media_type === "CLOCK" ? (
                        <i className="fa-solid fa-clock text-sm text-blue-500" />
                    ) : item.layout_id ? (
                        <i className="bx bxs-layout text-sm text-purple-500" />
                    ) : item.playlist_id ? (
                        <i className="fas fa-list-ul text-xs text-cyan-500" />
                    ) : (item.media_type === "IMAGE" || item.media_type === "VIDEO") ? (
                        <LazyImage media={item} popupType="playlist_media_setting" index={item.id} styleClass="w-full h-full object-cover" />
                    ) : (
                        <i className="bx bx-file text-sm text-slate-400" />
                    )}
                </div>

                {/* Text info */}
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className={`text-[11px] font-semibold truncate leading-tight ${isActive ? 'text-[var(--pp-btn-honey)]' : 'text-[var(--pp-text-primary)]'}`}>
                        {item.media_name || item.playlist_name || item.layout_name || t('show.show_untitled')}
                    </span>
                    <div className="flex items-center gap-1.5">
                        <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider ${tc.text}`}>
                            {tc.label}
                        </span>
                        <span className="text-[var(--pp-border-strong)] text-[10px]">·</span>
                        <span className={`text-[10px] font-mono font-semibold ${isActive ? 'text-[var(--pp-btn-honey)]' : 'text-[var(--pp-text-secondary)]'}`}>
                            {isActive ? (() => {
                                const totalSec = item.custom_duration || 0;
                                const elapsedMs = isPaused
                                    ? pausedElapsedRef.current
                                    : (startTime ? Math.max(0, new Date().getTime() - startTime.getTime()) : 0);
                                const elapsedSec = Math.min(elapsedMs / 1000, totalSec);
                                return <>{formatDuration(elapsedSec)}<span className="text-[var(--pp-btn-honey-hover)] mx-0.5">/</span>{formatDuration(totalSec)}</>;
                            })() : formatDuration(item.custom_duration)}
                        </span>
                    </div>
                </div>

                {/* Right: chevron or playing bars */}
                {hasChildren ? (
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-all ${isActive ? 'bg-[color-mix(in_srgb,var(--pp-btn-honey)_10%,transparent)] text-[var(--pp-btn-honey)]' : 'text-[var(--pp-text-secondary)]'}`}>
                        <i className={`pi pi-chevron-down text-[9px] transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                    </div>
                ) : isActive ? (
                    <div className="flex-shrink-0 flex items-center gap-[3px]">
                        {[0, 1, 2].map(i => (
                            <div key={i} className="w-[2px] h-3 bg-[var(--pp-btn-honey)] rounded-full animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
                        ))}
                    </div>
                ) : null}
            </div>

            {/* Expandable children */}
            {hasChildren && expanded && (
                <div className="ml-4 border-l-2 border-[color-mix(in_srgb,var(--pp-btn-honey)_25%,white)] pl-3 py-1">
                    {item.playlist_name ? (
                        <PlaylistContent
                            showAll
                            itIsPlaying={currentItem?.id === item?.playlist_id}
                            item={item}
                            startTime={startTime}
                            pause_start_time={startTime}
                        />
                    ) : (
                        <LayoutsContent
                            showAll
                            itIsPlaying={currentItem?.id === item?.layout_id}
                            item={item}
                            startTime={startTime}
                            pause_start_time={startTime}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function PreviewCampaign() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [startTime, setStartTime] = useState();
    const [tick, setTick] = useState(0);
    const [disableForward, setDisableForward] = useState(false);
    const [disableBackward, setDisableBackward] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    // stores elapsed ms at the moment we paused, so progress freezes
    const pausedElapsedRef = useRef(0);
    const isPausedRef = useRef(false);
    const dispatch = useDispatch();

    // Sync with Redux paused state (triggered when user pauses the native video player)
    const reduxPaused = useSelector((state) => state.device.paused[PREVIEW_AREA_ID]);
    useEffect(() => {
        if (reduxPaused === undefined) return;
        // Only act when Redux state differs from our local state
        setIsPaused(prev => {
            if (!!reduxPaused === prev) return prev; // no change
            const nowPausing = !!reduxPaused;
            isPausedRef.current = nowPausing;
            if (nowPausing && startTime) {
                pausedElapsedRef.current = new Date().getTime() - startTime.getTime();
            } else if (!nowPausing && startTime) {
                const frozenElapsed = pausedElapsedRef.current;
                setStartTime(new Date(new Date().getTime() - frozenElapsed));
            }
            return nowPausing;
        });
    }, [reduxPaused]);

    const { isLoading, data } = usePreviewCampaign(id, {
        enabled: !!id,
    });

    const currentItem = data?.mainContent?.[currentIndex];

    useEffect(() => {
        const interval = setInterval(() => {
            if (!isPausedRef.current) {
                setTick((t) => t + 1);
            }
        }, 100);

        return () => clearInterval(interval);
    }, []);


    useEffect(() => {
        if (data?.mainContent?.length && !startTime) {
            setStartTime(new Date());
        }
    }, [data?.mainContent, startTime]);


    const getProgressStyle = (startTime, duration) => {
        if (!startTime || !duration) return { background: "transparent" };

        const totalMs = duration * 1000;
        // When paused: use frozen elapsed; when playing: compute from real time
        const elapsedMs = isPaused
            ? pausedElapsedRef.current
            : new Date().getTime() - startTime.getTime();

        if (elapsedMs >= totalMs) {
            return { backgroundColor: "#8c86f15e" };
        }

        const percent = Math.max(1, Math.min(100, (elapsedMs / totalMs) * 100));

        return {
            background: `linear-gradient(to right, #8c86f15e ${percent}%, transparent ${percent}%)`,
            transition: isPaused ? 'none' : 'background 0.1s linear'
        };
    };


    useEffect(() => {
        if (!data?.mainContent?.length || !currentItem || !startTime) return;

        if (currentIndex === 0) {
            setDisableBackward(true);
        } else {
            setDisableBackward(false);
        }

        if (currentIndex === data.mainContent.length - 1) {
            setDisableForward(true);
        } else {
            setDisableForward(false);
        }

        // Don't auto-advance when paused
        if (isPaused) return;

        const durationMs = (currentItem.custom_duration || 10) * 1000;
        const now = new Date().getTime();
        const start = startTime.getTime();

        if (now - start >= durationMs) {
            const nextIndex = currentIndex + 1;
            pausedElapsedRef.current = 0;
            if (nextIndex < data.mainContent.length) {
                setCurrentIndex(nextIndex);
                setStartTime(new Date());
            } else {
                setCurrentIndex(0);
                setStartTime(new Date());
            }
        }
    }, [tick, currentIndex, startTime, data, isPaused]);


    const moveForward = () => {
        if (data?.mainContent?.length) {
            pausedElapsedRef.current = 0;
            isPausedRef.current = false;
            setIsPaused(false);
            dispatch(setPaused({ id: PREVIEW_AREA_ID, paused: false }));
            setCurrentIndex((prev) => (prev + 1) % data.mainContent.length);
            setStartTime(new Date());
        }
    };
    const moveBackward = () => {
        if (currentIndex > 0) {
            pausedElapsedRef.current = 0;
            isPausedRef.current = false;
            setIsPaused(false);
            dispatch(setPaused({ id: PREVIEW_AREA_ID, paused: false }));
            setCurrentIndex(currentIndex - 1);
            setStartTime(new Date());
        }
    };
    const togglePause = () => {
        setIsPaused(prev => {
            const nowPausing = !prev;
            isPausedRef.current = nowPausing;
            if (nowPausing && startTime) {
                pausedElapsedRef.current = new Date().getTime() - startTime.getTime();
            } else if (!nowPausing && startTime) {
                const frozenElapsed = pausedElapsedRef.current;
                setStartTime(new Date(new Date().getTime() - frozenElapsed));
            }
            // Also pause/resume the actual video element via Redux
            dispatch(setPaused({ id: PREVIEW_AREA_ID, paused: nowPausing }));
            return nowPausing;
        });
    };



    // Robust aspect ratio resolution
    const displayRatio = useMemo(() => {
        // Find aspect ratio from multiple possible locations in the returned data
        const findRatio = (obj) => {
            if (!obj) return null;
            // Handle if it's an array (sometimes API wraps it)
            const target = Array.isArray(obj) ? obj[0] : obj;
            if (target?.width && target?.height && parseFloat(target.width) > 0) {
                return { w: parseFloat(target.width), h: parseFloat(target.height) };
            }
            return null;
        };

        // Priority 1: Global layout/aspect ratio in the campaign data
        const gRatio = findRatio(data?.aspect_ratio) || 
                       findRatio(data?.layout?.aspect_ratio) || 
                       findRatio(data?.selectedLayout?.aspect_ratio);
        
        if (gRatio) return `${gRatio.w} / ${gRatio.h}`;

        if (!currentItem) return "16 / 9";
        
        // Priority 2: Item specific ratio
        const iRatio = findRatio(currentItem.aspect_ratio) || 
                       findRatio(currentItem.layout?.aspect_ratio);
        
        if (iRatio) return `${iRatio.w} / ${iRatio.h}`;

        // Priority 3: Raw fields
        let rw = currentItem.aspect_ratio_w || (parseFloat(currentItem.width) !== 100 ? currentItem.width : null);
        let rh = currentItem.aspect_ratio_h || (parseFloat(currentItem.height) !== 100 ? currentItem.height : null);

        if (rw && rh && parseFloat(rw) > 0 && parseFloat(rh) > 0) {
            return `${parseFloat(rw)} / ${parseFloat(rh)}`;
        }
        
        return "16 / 9";
    }, [currentItem, data]);

    const isPortrait = useMemo(() => {
        const parts = displayRatio.split("/").map(p => parseFloat(p.trim()));
        return parts.length === 2 && parts[1] > parts[0];
    }, [displayRatio]);


    return (
        <div className="flex flex-col lg:flex-row lg:h-full lg:min-h-screen bg-[var(--pp-bg-secondary)] text-[var(--pp-text-primary)] lg:overflow-hidden font-sans">

            {/* Left Side: Next-Gen Display Area */}
            <div className="flex-1 flex flex-col relative overflow-hidden bg-[var(--pp-bg-secondary)] min-h-[55vh] lg:min-h-0">
                 {/* Dynamic Abstract Backdrop */}
                 <div className="absolute inset-0 pointer-events-none overflow-hidden">
                     <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[color-mix(in_srgb,var(--pp-btn-honey)_9%,transparent)] rounded-full blur-[100px] animate-pulse"></div>
                     <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-200/30 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
                     <div className="absolute inset-0 opacity-[0.3]" 
                         style={{ backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', backgroundSize: '32px 32px' }}
                     ></div>
                 </div>

                {/* Main Viewport */}
                <div className="flex-1 flex flex-col items-center justify-center p-4 lg:p-12 relative z-10">
                    
                    {/* Screen Device Wrapper */}
                    <div className="relative group w-full max-w-6xl flex flex-col items-center justify-center perspective-1000">
                        <div 
                            className="relative bg-black rounded-2xl overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5),0_30px_60px_-30px_rgba(0,0,0,0.6)] ring-8 ring-slate-950 transition-all duration-700 ease-out transform group-hover:scale-[1.005]" 
                            style={{ 
                                aspectRatio: displayRatio,
                                maxHeight: 'calc(100vh - 200px)', 
                                height: isPortrait ? 'calc(100vh - 200px)' : 'auto',
                                width: isPortrait ? 'auto' : '100%',
                                maxWidth: '100%',
                                minWidth: '320px'
                            }}
                        >
                            {/* Content (no tint/vignette overlay — media renders at full clarity) */}
                            <div className="relative z-10 w-full h-full bg-black">
                                <ContentPreview 
                                    currentItem={currentItem} 
                                    startTime={startTime} 
                                    onEnded={moveForward}
                                />
                            </div>
                        </div>

                        {/* Player Controls - Moved Below Screen */}
                        <div className="mt-8 flex items-center gap-5 px-8 py-3.5 bg-white/70 backdrop-blur-2xl border border-white/50 ring-1 ring-slate-200/50 rounded-full shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] z-50 transition-all duration-300 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)] hover:scale-105">

                            {/* Backward */}
                            <button
                                onClick={moveBackward}
                                disabled={disableBackward}
                                className={`flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200 ${disableBackward ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[color-mix(in_srgb,var(--pp-btn-honey)_12%,white)] text-slate-600 hover:text-[var(--pp-btn-honey)] active:scale-95'}`}
                            >
                                <i className="fa-solid fa-backward-step text-base"></i>
                            </button>

                            {/* Play / Pause */}
                            <button
                                onClick={togglePause}
                                className="flex items-center justify-center w-11 h-11 rounded-full bg-[var(--pp-btn-honey)] hover:bg-[var(--pp-btn-honey-hover)] text-white shadow-md shadow-[color-mix(in_srgb,var(--pp-btn-honey)_25%,white)] active:scale-95 transition-all duration-200"
                            >
                                <i className={`fa-solid ${isPaused ? 'fa-play' : 'fa-pause'} text-sm`}></i>
                            </button>

                            {/* Info */}
                            <div className="flex flex-col items-center min-w-[140px] px-4 border-x border-slate-200/50">
                                <span className="text-[9px] font-extrabold tracking-[0.2em] uppercase text-[var(--pp-btn-honey)] mb-0.5">
                                    {isPaused ? t('show.show_paused') : (currentItem?.media_type || t('show.show_now_playing'))}
                                </span>
                                {currentItem ? (() => {
                                    const totalSec = currentItem.custom_duration || 0;
                                    const elapsedMs = isPaused
                                        ? pausedElapsedRef.current
                                        : (startTime ? Math.max(0, new Date().getTime() - startTime.getTime()) : 0);
                                    const elapsedSec = Math.min(elapsedMs / 1000, totalSec);
                                    return (
                                        <span className="text-sm font-bold text-slate-800 tabular-nums font-mono">
                                            {formatDuration(elapsedSec)}
                                            <span className="text-slate-400 font-normal mx-1">/</span>
                                            {formatDuration(totalSec)}
                                        </span>
                                    );
                                })() : (
                                    <span className="text-sm font-bold text-slate-800 tabular-nums">--:--</span>
                                )}
                            </div>

                            {/* Forward */}
                            <button
                                onClick={moveForward}
                                disabled={disableForward}
                                className={`flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200 ${disableForward ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[color-mix(in_srgb,var(--pp-btn-honey)_12%,white)] text-slate-600 hover:text-[var(--pp-btn-honey)] active:scale-95'}`}
                            >
                                <i className="fa-solid fa-forward-step text-base"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Minimalist Footer Bar */}
                <div className="w-full bg-white/60 backdrop-blur-md border-t border-slate-100 px-8 py-3 flex items-center justify-between z-20">
                     <div className="flex items-center gap-4">
                        <div className="h-9 w-9 rounded-lg bg-white border border-slate-100 flex items-center justify-center text-[var(--pp-btn-honey)] shadow-sm">
                             <i className={`text-lg ${currentItem?.layout_id ? 'bx bxs-layout' : currentItem?.playlist_id ? 'bx bxs-playlist' : 'bx bx-play-circle'}`}></i>
                        </div>
                        <div className="flex items-center gap-2">
                             <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('form.title')}:</span>
                             <h2 className="text-sm font-semibold text-slate-700 truncate max-w-[300px]" title={currentItem?.media_name}>
                                {currentItem?.media_name || currentItem?.playlist_name || currentItem?.layout_name || t('messages.loading')}
                            </h2>
                        </div>
                     </div>
                     
                     <div className="flex items-center gap-2 text-xs font-medium text-slate-400">
                        <span className="bg-slate-100 px-2 py-1 rounded-md text-slate-500">
                            {t('show.show_item_counter', { current: currentItem ? currentIndex + 1 : 0, total: data?.mainContent?.length || 0 })}
                        </span>
                     </div>
                </div>
            </div>

            {/* Right Side: Ultra-Modern Playlist Sidebar */}
            <div className="flex w-full lg:w-[400px] bg-[var(--pp-bg-elevated)] border-t lg:border-t-0 lg:border-l border-[var(--pp-border)] flex-col shadow-xl z-30 relative max-h-[65vh] lg:max-h-none">
                {/* Header */}
                <div className="p-5 lg:p-6 border-b border-[var(--pp-border)] bg-[var(--pp-bg-elevated)] z-10 sticky top-0">
                    <div className="flex items-center justify-between mb-1 gap-2">
                        <h3 className="text-lg lg:text-xl font-bold text-[var(--pp-text-primary)] tracking-tight flex items-center gap-2">
                           {t('show.show_up_next')}
                        </h3>
                        <span className="px-2 py-0.5 rounded-md bg-[var(--pp-bg-tertiary)] text-[10px] font-bold text-[var(--pp-text-secondary)] uppercase tracking-widest shrink-0">
                            {t('show.show_queue')}
                        </span>
                    </div>
                    <p className="text-xs text-[var(--pp-text-secondary)] font-medium">
                        {t('show.show_items_in_sequence', { count: data?.mainContent?.length || 0 })}
                    </p>
                </div>

                {/* List Container */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1.5 bg-[var(--pp-bg-secondary)]">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-40 gap-3 opacity-60">
                            <ProgressSpinner style={{ width: "30px", height: "30px" }} strokeWidth="3" fill="transparent" animationDuration="1s" />
                            <span className="text-xs font-semibold text-[var(--pp-text-secondary)] uppercase tracking-wider">{t('show.show_loading_playlist')}</span>
                        </div>
                    ) : !data?.mainContent?.length ? (
                         <div className="flex flex-col items-center justify-center h-48 text-[var(--pp-text-secondary)] gap-3">
                            <div className="h-16 w-16 rounded-full bg-[var(--pp-bg-tertiary)] flex items-center justify-center">
                                <i className="bx bx-ghost text-3xl opacity-30"></i>
                            </div>
                            <span className="text-sm font-medium">{t('show.show_no_media_items')}</span>
                         </div>
                    ) : (
                        data.mainContent.map((item, index) => {
                            const isActive = currentIndex === index;
                            const itemType = item.layout_id ? 'layout' : item.playlist_id ? 'playlist' : item.original_media_type?.toLowerCase() || item.media_type?.toLowerCase() || 'media';
                            const typeColors = {
                                layout:   { bg: 'bg-purple-50',  text: 'text-purple-600',  border: 'border-purple-100',  label: 'Layout'   },
                                playlist: { bg: 'bg-cyan-50',    text: 'text-cyan-600',    border: 'border-cyan-100',    label: 'Playlist' },
                                weather:  { bg: 'bg-amber-50',   text: 'text-amber-500',   border: 'border-amber-100',   label: 'Weather'  },
                                clock:    { bg: 'bg-blue-50',    text: 'text-blue-500',    border: 'border-blue-100',    label: 'Clock'    },
                                image:    { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100', label: 'Image'    },
                                video:    { bg: 'bg-rose-50',    text: 'text-rose-500',    border: 'border-rose-100',    label: 'Video'    },
                            };
                            const tc = typeColors[itemType] || { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-100', label: itemType };

                            const hasChildren = !!(item.playlist_id || item.layout_id);

                            return (
                                <SidebarItem
                                    key={index}
                                    index={index}
                                    item={item}
                                    isActive={isActive}
                                    tc={tc}
                                    hasChildren={hasChildren}
                                    tick={tick}
                                    isPaused={isPaused}
                                    pausedElapsedRef={pausedElapsedRef}
                                    startTime={startTime}
                                    getProgressStyle={getProgressStyle}
                                    currentItem={currentItem}
                                    onSelect={() => {
                                        setCurrentIndex(index);
                                        setStartTime(new Date());
                                    }}
                                />
                            );

                        })
                    )}
                </div>
            </div>
        </div>
    );
}

export default PreviewCampaign;
